import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import { GlassCard, GlassButton, GlassBadge } from '../../components/ui/GlassComponents';
import { countWorkingDays } from '../../utils/leaveDayCount';

const LeaveApprovals = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pendingLeaves, setPendingLeaves] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [workingDays, setWorkingDays] = useState(null); // util defaults to Mon-Fri

  useEffect(() => {
    fetchPendingLeaves();
    // Pick up team workingDays so the "X days" badge matches what the server
    // will deduct on approval. Soft failure — util defaults to Mon-Fri.
    axios.get('/api/teams/current')
      .then(r => {
        if (r.data && Array.isArray(r.data.workingDays)) setWorkingDays(r.data.workingDays);
      })
      .catch(() => {});
  }, []);

  const fetchPendingLeaves = async () => {
    setLoading(true);
    try {
      // Fetch leave types first to ensure we have them for mapping
      const typesResponse = await axios.get('/api/leaves/types');
      const types = typesResponse.data || [];
      setLeaveTypes(types);
      console.log('Leave types:', types);
      
      // Fetch pending leaves for the team
      const response = await axios.get('/api/leaves?status=pending');
      const leaves = response.data.leaves || [];
      console.log('Pending leaves data:', leaves);
      
      setPendingLeaves(leaves);
    } catch (error) {
      console.error('Error fetching pending leaves:', error);
      toast.error('Failed to load pending leave requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveReject = async (id, status) => {
    setProcessingId(id);
    try {
      await axios.put(`/api/leaves/${id}`, { status });
      toast.success(`Leave request ${status} successfully`);
      
      // Remove the processed leave from the list
      setPendingLeaves(prev => prev.filter(leave => leave.id !== id));
    } catch (error) {
      console.error(`Error ${status === 'approved' ? 'approving' : 'rejecting'} leave request:`, error);
      // Prefer the server's message (e.g. manager self-approval explanation)
      // and only fall back to the generic toast if the server didn't send one.
      const verb = status === 'approved' ? 'approve' : 'reject';
      toast.error(error.response?.data?.message || `Failed to ${verb} leave request`);
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateDuration = (startDate, endDate, halfDay) => {
    const count = countWorkingDays({ startDate, endDate, workingDays, halfDay });
    return `${count} ${count === 1 ? 'day' : 'days'}`;
  };

  // Get the display name for a leave type (standard or custom)
  const getLeaveTypeName = (leave) => {
    // If the backend already provided a typeName, use it
    if (leave.typeName) {
      return leave.typeName;
    }
    
    // For standard leave types
    if (leave.type) {
      return leave.type.charAt(0).toUpperCase() + leave.type.slice(1);
    }
    
    // For custom leave types, look up the name from our leave types
    if (leave.customLeaveTypeId) {
      const customType = leaveTypes.find(t => t.id === leave.customLeaveTypeId);
      if (customType) {
        return customType.name;
      }
      return 'Custom';
    }
    
    return 'Unknown';
  };

  const getLeaveTypeColor = (leave) => {
    // If we have a typeColor from the backend, use it to generate Tailwind classes
    if (leave && leave.typeColor && typeof leave === 'object') {
      // Extract color name from hex code or use the color name directly
      let colorName = 'blue';
      
      // Map common hex codes to Tailwind color names
      const colorMap = {
        '#4CAF50': 'green',
        '#E91E63': 'red',
        '#2563EB': 'blue',
        '#059669': 'green',
        '#7C3AED': 'purple',
        '#6B7280': 'gray'
      };
      
      if (colorMap[leave.typeColor]) {
        colorName = colorMap[leave.typeColor];
      }
      
      return `bg-${colorName}-100 text-${colorName}-800 dark:bg-${colorName}-900 dark:text-${colorName}-200`;
    }
    
    // For custom leave types, look up the color from our leave types
    if (leave.customLeaveTypeId) {
      const customType = leaveTypes.find(t => t.id === leave.customLeaveTypeId);
      if (customType && customType.color) {
        const colorName = customType.color === '#4CAF50' ? 'green' : 
                         customType.color === '#E91E63' ? 'red' : 
                         customType.color === '#2563EB' ? 'blue' : 
                         customType.color === '#059669' ? 'green' : 
                         customType.color === '#7C3AED' ? 'purple' : 'blue';
        return `bg-${colorName}-100 text-${colorName}-800 dark:bg-${colorName}-900 dark:text-${colorName}-200`;
      }
    }
    
    // Otherwise, try to find the leave type in the team's leave types
    const typeId = leave && typeof leave === 'object' ? leave.type || leave.customLeaveTypeId : leave;
    const leaveType = leaveTypes.find(lt => lt.id === typeId);
    
    if (leaveType && leaveType.color) {
      // Map the color to a Tailwind class based on the hex color
      const colorName = leaveType.color === '#4CAF50' ? 'green' :
                       leaveType.color === '#E91E63' ? 'red' :
                       leaveType.color === '#2563EB' ? 'blue' :
                       leaveType.color === '#059669' ? 'green' :
                       leaveType.color === '#7C3AED' ? 'purple' : 'blue';
      return `bg-${colorName}-100 text-${colorName}-800 dark:bg-${colorName}-900 dark:text-${colorName}-200`;
    }
    
    // Default fallback
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-indigo-900">
      <div className="container mx-auto px-4 py-8">
        <GlassCard className="p-6 animate-fade-in">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-6">
            Pending Leave Approvals
          </h1>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
          </div>
        ) : pendingLeaves.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-lg font-medium text-gray-900 dark:text-white">No pending leave requests</h3>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              All leave requests have been processed.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Employee
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Type
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Duration
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Dates
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Notes
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {pendingLeaves.map(leave => (
                  <tr key={leave.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {leave.User?.firstName} {leave.User?.lastName}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {leave.User?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getLeaveTypeColor(leave)}`}>
                        {getLeaveTypeName(leave)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {calculateDuration(leave.startDate, leave.endDate, leave.halfDay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(leave.startDate)} - {formatDate(leave.endDate)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                      {leave.notes || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleApproveReject(leave.id, 'approved')}
                          disabled={processingId === leave.id}
                          className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                        >
                          {processingId === leave.id ? 'Processing...' : 'Approve'}
                        </button>
                        <button
                          onClick={() => handleApproveReject(leave.id, 'rejected')}
                          disabled={processingId === leave.id}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                        >
                          {processingId === leave.id ? 'Processing...' : 'Reject'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        <div className="mt-6 flex justify-end">
          <button
            onClick={fetchPendingLeaves}
            className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Refresh
          </button>
        </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default LeaveApprovals;
